import client from './client';

/**
 * Sessions API
 *
 * All endpoints map to /api/sessions on the Spring Boot backend.
 */

/**
 * Create a new interview session.
 *
 * Backend now reads the host identity from the JWT, so the request body is empty.
 * Response: { sessionId, code, hostToken, livekitToken, livekitUrl }
 */
export const createSession = () => {
  console.log('[sessions] createSession (host derived from JWT)');
  return client.post('/api/sessions', {});
};

/**
 * Join an existing session as a candidate (public - no auth required).
 * @param {{ sessionCode: string, candidateName: string, candidateEmail?: string }} data
 */
export const joinSession = (data) => {
  console.log('[sessions] joinSession', data);
  return client.post('/api/sessions/join', data);
};

/**
 * Get session details by ID (requires auth).
 * @param {string|number} id
 */
export const getSession = (id) => {
  console.log('[sessions] getSession', id);
  return client.get(`/api/sessions/${id}`);
};

/**
 * End a session (requires HOST auth).
 * @param {string|number} id
 */
export const endSession = (id) => {
  console.log('[sessions] endSession', id);
  return client.post(`/api/sessions/${id}/end`);
};

/**
 * I-P0-C host recovery: release the agent device bound to a session so the candidate can
 * re-join from another device (e.g. their laptop failed). Detaches the current agent.
 * @param {string} id
 */
export const releaseAgentBinding = (id) => client.post(`/api/sessions/${id}/agent-binding/release`);

/**
 * List sessions for a host (requires auth).
 * @param {{ hostEmail?: string, orgId?: string, page?: number, size?: number, status?: string }} params
 */
export const listSessions = (params) => {
  console.log('[sessions] listSessions', params);
  return client.get('/api/sessions', { params });
};

/**
 * Get the integrity report for a completed session (requires auth).
 * @param {string|number} id
 */
export const getReport = (id) => {
  console.log('[sessions] getReport', id);
  return client.get(`/api/sessions/${id}/report`);
};

/**
 * Fetch the authenticated host's overview analytics (totals, top finding
 * categories, recent sessions). Powers /dashboard.
 */
export const getAnalytics = () => {
  console.log('[sessions] getAnalytics');
  return client.get('/api/sessions/analytics');
};

/**
 * Get a fresh LiveKit token for a session (requires auth).
 *
 * Backend: only {@param sessionCode} is sent - host vs candidate identity is derived from Spring
 * Security (ROLE_HOST vs candidate JWT), not query params.
 */
export const getLiveKitToken = (sessionCode) => {
  console.log('[sessions] getLiveKitToken', sessionCode);
  return client.get('/api/livekit/token', { params: { sessionCode } });
};

/**
 * Download the session report as a PDF blob (requires auth).
 * @param {string|number} id
 */
export const downloadReportPdf = (id) => {
  console.log('[sessions] downloadReportPdf', id);
  return client.get(`/api/sessions/${id}/report/pdf`, { responseType: 'blob' });
};

/**
 * Phase 8: issue a public 72h share token for the session report.
 * Returns { token, url, expiresAt }.
 */
export const shareReport = (id) => {
  console.log('[sessions] shareReport', id);
  return client.post(`/api/sessions/${id}/report/share`);
};

/**
 * Phase 8: fetch a session report via a public share token (no auth).
 */
export const getPublicReport = (token) => {
  console.log('[sessions] getPublicReport');
  return client.get(`/api/report/public/${encodeURIComponent(token)}`);
};

/**
 * Aggregated candidate directory for the authenticated host.
 * Returns an array of CandidateSummary objects:
 *   { candidateEmail, candidateName, totalSessions, totalFindings,
 *     worstRisk, firstSeen, lastSeen, lastStatus }
 */
export const listCandidates = () => {
  console.log('[sessions] listCandidates');
  return client.get('/api/sessions/candidates');
};

/**
 * Fetch a fresh session-scoped HOST JWT for the given session.
 * The dashboard WebSocket handshake requires role=HOST in the token;
 * the user's regular login token has role=INTERVIEWER and will be rejected.
 *
 * @param {string} id  session UUID
 * @returns {Promise<{ data: { hostToken: string } }>}
 */
export const getHostToken = (id) => {
  console.log('[sessions] getHostToken', id);
  return client.get(`/api/sessions/${id}/host-token`);
};
